<script setup>
import OverviewReportFilters from './OverviewReportFilters.vue';
import ReportSectionCard from './ReportSectionCard.vue';
import ReportDataTable from './ReportDataTable.vue';
import ReportEmptyState from './ReportEmptyState.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { formatTime } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
} from '@tanstack/vue-table';
import { computed, onMounted, ref, h } from 'vue';
import { useI18n } from 'vue-i18n';
import SummaryReportLink from './SummaryReportLink.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'account',
  },
  getterKey: {
    type: String,
    default: '',
  },
  actionKey: {
    type: String,
    default: '',
  },
  summaryKey: {
    type: String,
    default: '',
  },
  fetchItemsKey: {
    type: String,
    required: true,
  },
});

const store = useStore();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const flagMap = {
  agent: 'isFetchingAgentSummaryReports',
  inbox: 'isFetchingInboxSummaryReports',
  team: 'isFetchingTeamSummaryReports',
  label: 'isFetchingLabelSummaryReports',
};

const uiFlags = useMapGetter('summaryReports/getUIFlags');
const isLoading = computed(() => uiFlags.value[flagMap[props.type]] ?? false);

const rowItems = useMapGetter([props.getterKey]) || [];
const reportMetrics = useMapGetter([props.summaryKey]) || [];

const getMetrics = id =>
  reportMetrics.value.find(metrics => metrics.id === Number(id)) || {};
const columnHelper = createColumnHelper();
const { t } = useI18n();

const TYPE_ICONS = {
  agent: 'i-hugeicons-headset',
  inbox: 'i-hugeicons-inbox',
  team: 'i-hugeicons-user-group',
  label: 'i-hugeicons-tag-01',
};

const typeKey = computed(() => props.type.toUpperCase());

const renderAvgTime = cellProps => {
  const value = cellProps.getValue();
  if (value === undefined) {
    return h('span', { class: 'text-n-slate-10' }, '—');
  }
  return h('span', { class: 'text-n-slate-12' }, formatTime(value));
};

const renderCount = cellProps => {
  const value = cellProps.getValue() || 0;
  return h(
    'span',
    { class: value ? 'font-medium text-n-slate-12' : 'text-n-slate-10' },
    Number(value).toLocaleString()
  );
};

// Missing averages sort after every measured value, in both directions.
const numericColumn = (key, header, cell) =>
  columnHelper.accessor(key, {
    header,
    cell,
    size: 170,
    sortUndefined: 'last',
    sortDescFirst: true,
    meta: { align: 'end' },
  });

const columns = computed(() => [
  columnHelper.accessor('name', {
    header: t(`SUMMARY_REPORTS.${typeKey.value}`),
    cell: cellProps => h(SummaryReportLink, cellProps),
    sortingFn: (rowA, rowB) =>
      String(rowA.original.name || '').localeCompare(
        String(rowB.original.name || ''),
        undefined,
        { sensitivity: 'base' }
      ),
  }),
  numericColumn(
    'conversationsCount',
    t('SUMMARY_REPORTS.CONVERSATIONS'),
    renderCount
  ),
  numericColumn(
    'avgFirstResponseTime',
    t('SUMMARY_REPORTS.AVG_FIRST_RESPONSE_TIME'),
    renderAvgTime
  ),
  numericColumn(
    'avgResolutionTime',
    t('SUMMARY_REPORTS.AVG_RESOLUTION_TIME'),
    renderAvgTime
  ),
  numericColumn(
    'avgReplyTime',
    t('SUMMARY_REPORTS.AVG_REPLY_TIME'),
    renderAvgTime
  ),
  numericColumn(
    'resolutionsCount',
    t('SUMMARY_REPORTS.RESOLUTION_COUNT'),
    renderCount
  ),
]);

const toMetricValue = value =>
  value === null || value === undefined || value === ''
    ? undefined
    : Number(value);

const tableData = computed(() =>
  (rowItems.value || []).map(row => {
    const rowMetrics = getMetrics(row.id);
    const {
      conversationsCount,
      avgFirstResponseTime,
      avgResolutionTime,
      avgReplyTime,
      resolvedConversationsCount,
    } = rowMetrics;
    return {
      id: row.id,
      // we fallback on title, label for instance does not have a name property
      name: row.name ?? row.title,
      type: props.type,
      subtitle: row.email || row.description || '',
      thumbnail: row.thumbnail || row.avatar_url || '',
      status: row.availability_status || null,
      color: row.color || '',
      inbox: props.type === 'inbox' ? row : null,
      conversationsCount: Number(conversationsCount || 0),
      avgFirstResponseTime: toMetricValue(avgFirstResponseTime),
      avgReplyTime: toMetricValue(avgReplyTime),
      avgResolutionTime: toMetricValue(avgResolutionTime),
      resolutionsCount: Number(resolvedConversationsCount || 0),
    };
  })
);

const hasRows = computed(() => tableData.value.length > 0);

const fetchReportsWithRetry = async () => {
  const params = {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  };
  try {
    await store.dispatch(props.actionKey, params);
  } catch {
    try {
      await store.dispatch(props.actionKey, params);
    } catch {
      useAlert(t('REPORT.SUMMARY_FETCHING_FAILED'));
    }
  }
};

const fetchAllData = () => {
  store.dispatch(props.fetchItemsKey);
  fetchReportsWithRetry();
};

onMounted(() => fetchAllData());

const onFilterChange = updatedFilter => {
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  businessHours.value = updatedFilter.businessHours;
  fetchAllData();
};

const sorting = ref([]);

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  state: {
    get sorting() {
      return sorting.value;
    },
  },
  onSortingChange: updater => {
    sorting.value =
      typeof updater === 'function' ? updater(sorting.value) : updater;
  },
  enableSorting: true,
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
});

// downloadReports method is not used in this component
// but it is exposed to be used in the parent component
const downloadReports = () => {
  const dispatchMethods = {
    agent: 'downloadAgentReports',
    label: 'downloadLabelReports',
    inbox: 'downloadInboxReports',
    team: 'downloadTeamReports',
  };
  if (dispatchMethods[props.type]) {
    const fileName = generateFileName({
      type: props.type,
      to: to.value,
      businessHours: businessHours.value,
    });
    const params = {
      from: from.value,
      to: to.value,
      fileName,
      businessHours: businessHours.value,
    };
    store.dispatch(dispatchMethods[props.type], params);
  }
};

defineExpose({ downloadReports });
</script>

<template>
  <div class="flex flex-col gap-4 pb-6">
    <OverviewReportFilters
      :disabled="isLoading"
      @filter-change="onFilterChange"
    />
    <ReportSectionCard
      :title="t(`SUMMARY_REPORTS.TABLE_TITLE.${typeKey}`)"
      :description="t('SUMMARY_REPORTS.TABLE_DESCRIPTION')"
      :icon="TYPE_ICONS[type] || 'i-hugeicons-table-01'"
      flush
    >
      <template v-if="hasRows" #actions>
        <span
          class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs font-medium tabular-nums text-n-slate-11"
        >
          {{ t(`SUMMARY_REPORTS.COUNT.${typeKey}`, tableData.length) }}
        </span>
      </template>
      <div class="relative min-h-[8rem]">
        <div
          v-if="isLoading && !hasRows"
          class="flex flex-col gap-3 px-5 pb-5 pt-1"
          role="status"
          :aria-label="t('SUMMARY_REPORTS.LOADING')"
        >
          <div
            v-for="item in 5"
            :key="item"
            class="h-11 rounded-lg bg-n-alpha-2 motion-safe:animate-pulse"
          />
        </div>
        <ReportEmptyState
          v-else-if="!hasRows"
          :icon="TYPE_ICONS[type] || 'i-hugeicons-database'"
          :title="t(`SUMMARY_REPORTS.EMPTY.${typeKey}`)"
          :description="t('SUMMARY_REPORTS.EMPTY_DESCRIPTION')"
        />
        <ReportDataTable
          v-else
          :table="table"
          min-width-class="min-w-[58rem]"
          class="rounded-b-2xl"
        />
        <Transition
          enter-active-class="transition-opacity duration-300 ease-out motion-reduce:transition-none"
          leave-active-class="transition-opacity duration-200 ease-in motion-reduce:transition-none"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            v-if="isLoading && hasRows"
            class="pointer-events-none absolute inset-0 flex justify-center rounded-b-2xl bg-n-solid-1/70 pt-16"
            role="status"
            :aria-label="t('SUMMARY_REPORTS.LOADING')"
          >
            <Spinner :size="28" class="text-n-brand" />
          </div>
        </Transition>
      </div>
    </ReportSectionCard>
  </div>
</template>
